import { ICommandBarItemProps, ThemeProvider } from '@fluentui/react';
import { DarkTheme } from '@fluentui/theme-samples';
import { IRadialMenuItem, RadialMenu } from 'components/controls/RadialMenu';
import React, { useEffect, useMemo } from 'react';
import {
    QUICK_NAV_FLOATING_BACKDROP_HIDDEN,
    QUICK_NAV_FLOATING_BACKDROP_VISIBLE,
} from '../Pharmacy/PharmacyPlan/PharmacyPlan.constants';

interface ICompareContextMenuRadialOverlayProps {
    isHidden: boolean;
    contextMenuItems: ICommandBarItemProps[];
    onDismiss?: () => void;
}

const CompareContextMenuRadialOverlay: React.FC<ICompareContextMenuRadialOverlayProps> = React.memo((props) => {
    const { isHidden, contextMenuItems, onDismiss } = props;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss?.();
            }
        };

        if (onDismiss && !isHidden) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isHidden, onDismiss]);

    const menuItems: IRadialMenuItem[] = useMemo(() => {
        const toRadialItem = (item: ICommandBarItemProps): IRadialMenuItem | null => {
            if (item.disabled) {
                return null;
            }

            const childItems = (item.subMenuProps?.items ?? [])
                .map(toRadialItem)
                .filter((child): child is IRadialMenuItem => !!child);

            if (childItems.length > 0) {
                return {
                    key: `context-${item.key}`,
                    label: item.text ?? item.key,
                    items: childItems,
                };
            }

            if (!item.onClick) {
                return null;
            }

            return {
                key: `context-${item.key}`,
                label: item.text ?? item.key,
                onClick: () => {
                    item.onClick?.(undefined, item);
                },
            };
        };

        return contextMenuItems
            .map(toRadialItem)
            .filter((item): item is IRadialMenuItem => !!item);
    }, [contextMenuItems]);

    return (
        <ThemeProvider theme={DarkTheme}>
            <div
                style={isHidden ? QUICK_NAV_FLOATING_BACKDROP_HIDDEN : QUICK_NAV_FLOATING_BACKDROP_VISIBLE}
                onClick={onDismiss}
            />
            <RadialMenu items={menuItems} isOpen={!isHidden} onDismiss={onDismiss} centerLabel="Menu" />
        </ThemeProvider>
    );
});

export default CompareContextMenuRadialOverlay;
